import { axios } from '@lib/axios';

import { FileUploadParams, UploadProcess } from './types';

import { FileService } from './FileService';

export interface UploadedPart {
    etag: string;
    partNumber: number;
}

interface ChunkUploadedData {
    uploadedPart: UploadedPart;
    progress: number;
}

const BASE_URL = '/api/file-service';

export class S3Service extends FileService {
    public async getDownloadLink(path: string, isOpenInBrowser?: boolean, downloadFileName?: string): Promise<string> {
        const res = await axios.post(`${BASE_URL}/download-link`, {
            isOpenInBrowser,
            fileName: path,
            downloadFileName,
        });
        return res.data.link;
    }

    public async makeSharedLink(path: string): Promise<string> {
        const res = await axios.post(`${BASE_URL}/shared-link`, { fileName: path });
        return res.data.link;
    }

    public async getSharedLink(path: string, downloadFileName?: string): Promise<string> {
        const res = await axios.get(`${BASE_URL}/shared-link`, { params: { fileName: path, downloadFileName } });
        return res.data.link;
    }

    public async initMultipartUpload(params: FileUploadParams): Promise<string> {
        const res = await axios.post(`${BASE_URL}/multipart-upload-init`, params);
        return res.data.uploadId;
    }

    public async uploadMultipartPart(
        params: FileUploadParams,
        chunk: Blob,
        uploadId: string,
        partNumber: number,
    ): Promise<UploadedPart> {
        const data = { ...params, uploadId, partNumber };
        const { etag } = await this.sendFile(`${BASE_URL}/multipart-upload`, chunk, data);
        return { etag, partNumber };
    }

    public async completeMultipartUpload(
        params: FileUploadParams,
        uploadId: string,
        uploadedParts: UploadedPart[],
    ): Promise<void> {
        await axios.post(`${BASE_URL}/multipart-upload-complete`, {
            ...params,
            uploadId,
            uploaded: uploadedParts,
        });
    }

    protected async whollyUpload(file: File, params: FileUploadParams): Promise<void> {
        await this.sendFile(`${BASE_URL}/upload-whole-file`, file, params);
    }

    protected async *multipartUploadProcess(
        chunks: Blob[],
        params: FileUploadParams,
    ): AsyncIterableIterator<UploadProcess> {
        const uploadId = await this.initMultipartUpload(params);

        const uploadedParts: UploadedPart[] = [];
        for await (const { uploadedPart, progress } of this.chunksUploadProcess(params, chunks, uploadId)) {
            uploadedParts.push(uploadedPart);
            yield { progress };
        }

        await this.completeMultipartUpload(params, uploadId, uploadedParts);
        return { progress: 100 };
    }

    private async *chunksUploadProcess(
        params: FileUploadParams,
        chunks: Blob[],
        uploadId: string,
    ): AsyncIterableIterator<ChunkUploadedData> {
        for (let partNumber = 1; partNumber <= chunks.length; partNumber++) {
            const uploadedPart = await this.uploadMultipartPart(params, chunks[partNumber - 1], uploadId, partNumber);
            yield {
                uploadedPart,
                progress: (partNumber / chunks.length) * 100,
            };
        }
    }
}
